// MessageCategoryFeedsById.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import CategoryBreadcrumb from '../component/CategoryBreadcrumb.jsx';
import SeoLongDescription from '../component/SeoLongDescription.jsx';
import CategoryNavScroll from '../component/CategoryNavScroll.jsx';

import media from '../style/media.js';
import { breakpoint, headerHeight, color } from '../style/variables.js';
import { CategoryListType } from '../resource/categoryConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { generateLangPath, getLangFromPath } from '../resource/langPath.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import defaults from '../resource/defaults.js';

const tabMetadata = {
  [CategoryListType.ALL]: {
    i18nKey: 'tab_all',
    buttonId: ButtonId.Category.TabAll,
  },
  [CategoryListType.STORY]: {
    i18nKey: 'stories',
    buttonId: ButtonId.Category.TabStory,
  },
  [CategoryListType.FLIX]: {
    i18nKey: 'title_flix',
    buttonId: ButtonId.Category.TabFlix,
  },
  [CategoryListType.SHORT]: {
    i18nKey: 'shorts',
    buttonId: ButtonId.Category.TabShort,
  },
  [CategoryListType.IMAGE]: {
    i18nKey: 'profile_photo',
    buttonId: ButtonId.Category.TabImage,
  },
};
const tabsStyle = {
  justifyContent: 'start',
};
const tabStyle = {
  marginRight: '28px',
  flex: 'none',
};
const lastTabStyle = {
  ...tabStyle,
  marginRight: 0,
};
const tabLabelStyle = {
  margin: 0,
  opacity: 1,
};

class MessageCategoryFeedsById extends React.PureComponent {
  handleTabChange = (event, value) => {
    event?.preventDefault();
    this.props.replace(this.getPath(value));
  };

  getPath = tabValue => {
    const { location } = this.props;
    const searchParams = new URLSearchParams(location.search);
    ['feed', '_feed', 'filters', '_filters', 'sorts', '_sorts'].forEach(key =>
      searchParams.delete(key)
    );

    const { categoryTabs, categoryId } = this.props;
    const listType = categoryTabs.includes(tabValue)
      ? tabValue
      : categoryTabs[0];

    let basePath = `/category/${categoryId}`;
    if (listType !== CategoryListType.ALL) {
      basePath += `/${listType}`;
    }

    const currentLang = getLangFromPath(location.pathname);
    const pathname = generateLangPath(basePath, currentLang);

    return {
      pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    };
  };

  renderTabs = () => {
    const { deviceWidth, t, categoryTabs, listType } = this.props;
    const isOnMobile = deviceWidth <= breakpoint.mobile;
    if (!isOnMobile) tabsStyle.paddingLeft = 20;

    return (
      <TabsWrapper>
        <Tabs
          value={listType}
          onChange={this.handleTabChange}
          isScrollable={isOnMobile}
          style={tabsStyle}
        >
          {(categoryTabs || []).map((tabKey, index) => {
            const tabData = tabMetadata[tabKey];
            if (!tabData) return null;
            const isActive = tabKey === listType;
            const title = t(tabData.i18nKey, {
              ns: TranslationNamespace.FEED,
            });
            const { pathname, search } = this.getPath(tabKey);
            const tabHref = `${pathname}${search}`;
            return (
              <Tab
                key={tabKey}
                value={tabKey}
                style={
                  categoryTabs.length - 1 === index ? lastTabStyle : tabStyle
                }
                data-element_id={tabMetadata[tabKey].buttonId}
                labelStyle={tabLabelStyle}
                label={<TabText isActive={isActive}>{title}</TabText>}
                href={tabHref}
              />
            );
          })}
        </Tabs>
      </TabsWrapper>
    );
  };

  render() {
    const { t, categoryId, listType, children, siblingCategoryIds } =
      this.props;
    const seoIntroKey = getTranslationKey({
      key: `seo_intro_template_description_category_${categoryId}`,
    });

    const seoIntroContent = t(seoIntroKey, { ns: TranslationNamespace.SEO });

    const footerDescKey = getTranslationKey({
      key: `seo_footer_long_description_template_category_${categoryId}`,
    });
    const footerDescContent = t(footerDescKey, {
      ns: TranslationNamespace.SEO,
    });

    return (
      <Container>
        <Header>
          <CategoryBreadcrumb categoryId={categoryId} listType={listType} />
          <HeaderText>
            {t(`category_${categoryId}`, {
              ns: TranslationNamespace.FEED,
            })}
          </HeaderText>
          <IntroDescription content={seoIntroContent} clampLines={1} />
          <CategoryNavScroll categoryIds={siblingCategoryIds} />
        </Header>
        {this.renderTabs()}
        <TabContent>{children}</TabContent>
        <LongDescriptionWrapper>
          <SeoLongDescription
            content={footerDescContent}
            clampLines={1}
            noSpaceWithContent
          />
        </LongDescriptionWrapper>
      </Container>
    );
  }
}

MessageCategoryFeedsById.propTypes = {
  deviceWidth: PropTypes.number,
  replace: PropTypes.func,
  location: PropTypes.object,
  t: PropTypes.func.isRequired,
  categoryTabs: PropTypes.array,
  categoryId: PropTypes.string,
  listType: PropTypes.string,
  siblingCategoryIds: PropTypes.array,
  children: PropTypes.node,
};

MessageCategoryFeedsById.defaultProps = {
  deviceWidth: 0,
  replace: () => null,
  goBack: () => null,
  categoryTabs: [],
  location: {},
  categoryId: '',
  listType: null,
  siblingCategoryIds: defaults.EMPTY_ARRAY,
  children: null,
};

const Container = styled.div`
  position: relative;
  margin: 0 auto 28px;
  max-width: 962px;

  border-right: 1px solid var(--background-color-secondary-on-dark, #232323);
  border-left: 1px solid var(--background-color-secondary-on-dark, #232323);
  background-color: var(--background-color-primary-on-dark, #191919);

  ${media.mobile`
    margin: unset;
    margin-bottom: 28px;
    width: 100%;
    border-right: unset;
    border-left: unset;
  `};
`;

const Header = styled.div`
  margin: calc(var(--space-to-pinned-notification) + ${headerHeight}px) auto 0;
  padding: 0px 16px;
`;

const HeaderText = styled.h1`
  font-size: 22px;
  font-weight: 600;
  line-height: 150%;
  margin: 0;
  padding: 0;
  letter-spacing: 0.022px;
`;

const TabsWrapper = styled.div`
  position: sticky;
  top: calc(${headerHeight}px + var(--space-to-pinned-notification));
  z-index: 2;
  background-color: ${color.black};
  border-top: 1px solid ${color.grey[900]};
  border-bottom: 1px solid ${color.grey[900]};
  ${media.tablet`
    border-top: none;
  `}
`;

const TabText = styled.h2`
  font-size: 16px;
  margin: 0;
  font-weight: normal;
  ${media.mobile`
    font-size: 14px;
  `};
`;

const TabContent = styled.div`
  background-color: ${color.black};
`;

const LongDescriptionWrapper = styled.div`
  max-width: 960px;
  width: 100%;
  padding-top: 8px;
  ${media.tablet`
    width: 100%;
    padding: 8px 16px 0;
  `};
`;

const IntroDescription = styled(SeoLongDescription)`
  ${media.mobile`
    padding-bottom: 8px;
  `};
`;

export default withTranslation()(MessageCategoryFeedsById);
